/**
 * Daily Word Selector
 * Handles deterministic daily word selection based on local date
 */

import { wordBankManager } from './wordBank'

export interface IDailyWordSelector {
  getCurrentWord(): Promise<string>
  getLocalDateString(): string
  calculateWordIndex(dateString: string): number
  checkForDateChange(): boolean
  isUsingFallbackDate(): boolean
  getDateCalculationErrors(): number
  setErrorNotificationCallback(callback: (message: string, isError: boolean) => void): void
}

export class DailyWordSelector implements IDailyWordSelector {
  private currentDate: string = ''
  private currentWord: string = ''
  private cachedWordIndex: number = -1
  private dateChangeListeners: Array<() => void> = []
  private dateCalculationErrors: number = 0
  private maxDateErrors: number = 3
  private usingFallbackDate: boolean = false
  private errorNotificationCallback?: (message: string, isError: boolean) => void

  /**
   * Set callback for error notifications
   * @param callback - Function to call when errors occur
   */
  setErrorNotificationCallback(callback: (message: string, isError: boolean) => void): void {
    this.errorNotificationCallback = callback
  }

  /**
   * Gets the current local date in YYYY-MM-DD format with comprehensive error handling
   * Uses en-CA locale for consistent formatting across browsers
   * @returns string - Date in YYYY-MM-DD format
   */
  getLocalDateString(): string {
    try {
      // Primary method: use en-CA locale for consistent YYYY-MM-DD format
      const localDate = new Date().toLocaleDateString('en-CA')
      
      // Validate the format
      if (!/^\d{4}-\d{2}-\d{2}$/.test(localDate)) {
        throw new Error(`Invalid date format: ${localDate}`)
      }
      
      // Validate the date is reasonable (not in far future/past)
      const parsedDate = new Date(localDate)
      const now = new Date()
      const yearDiff = Math.abs(parsedDate.getFullYear() - now.getFullYear())
      
      if (yearDiff > 10) {
        throw new Error(`Date seems unreasonable: ${localDate}`)
      }
      
      // Reset error count on successful calculation
      if (this.dateCalculationErrors > 0) {
        this.dateCalculationErrors = 0
        this.usingFallbackDate = false
        
        if (this.errorNotificationCallback) {
          this.errorNotificationCallback(
            'Date calculation restored successfully.',
            false
          )
        }
      }
      
      return localDate
    } catch (error) {
      this.dateCalculationErrors++
      console.warn(`Date calculation error (attempt ${this.dateCalculationErrors}):`, error)
      
      // Try fallback methods
      return this.getFallbackDate()
    }
  }

  /**
   * Get fallback date when primary date calculation fails
   * @returns string - Date in YYYY-MM-DD format
   */
  private getFallbackDate(): string {
    try {
      // Fallback 1: Manual formatting from local date
      const now = new Date()
      
      // Check if Date object is valid
      if (isNaN(now.getTime())) {
        throw new Error('Invalid Date object')
      }
      
      const year = now.getFullYear()
      const month = String(now.getMonth() + 1).padStart(2, '0')
      const day = String(now.getDate()).padStart(2, '0')
      
      // Validate components
      const monthNum = parseInt(month, 10)
      const dayNum = parseInt(day, 10)
      if (year < 2020 || year > 2100 || monthNum < 1 || monthNum > 12 || dayNum < 1 || dayNum > 31) {
        throw new Error(`Invalid date components: ${year}-${month}-${day}`)
      }
      
      const fallbackDate = `${year}-${month}-${day}`
      
      if (!this.usingFallbackDate && this.errorNotificationCallback) {
        this.errorNotificationCallback(
          'Using fallback date calculation due to timezone issues.',
          true
        )
      }
      
      this.usingFallbackDate = true
      // Using fallback local date
      return fallbackDate
      
    } catch (fallbackError) {
      console.warn('Local date fallback failed, using UTC:', fallbackError)
      
      try {
        // Fallback 2: UTC date as last resort
        const now = new Date()
        const year = now.getUTCFullYear()
        const month = String(now.getUTCMonth() + 1).padStart(2, '0')
        const day = String(now.getUTCDate()).padStart(2, '0')
        
        // Validate UTC components
        if (year < 2020 || year > 2100) {
          throw new Error(`Invalid UTC year: ${year}`)
        }
        
        const utcDate = `${year}-${month}-${day}`
        
        if (this.dateCalculationErrors <= this.maxDateErrors && this.errorNotificationCallback) {
          this.errorNotificationCallback(
            'Date calculation issues detected. Using UTC time as fallback.',
            true
          )
        }
        
        this.usingFallbackDate = true
        // Using UTC date as final fallback
        return utcDate
        
      } catch (utcError) {
        console.error('All date calculation methods failed:', utcError)
        
        // Final fallback: use a fixed date (today's date when this was written)
        const emergencyDate = '2024-01-01'
        
        if (this.dateCalculationErrors <= this.maxDateErrors && this.errorNotificationCallback) {
          this.errorNotificationCallback(
            'Critical date calculation error. Using emergency fallback date.',
            true
          )
        }
        
        this.usingFallbackDate = true
        // Using emergency fallback date
        return emergencyDate
      }
    }
  }

  /**
   * Check if currently using fallback date calculation
   * @returns boolean - True if using fallback, false if using normal calculation
   */
  isUsingFallbackDate(): boolean {
    return this.usingFallbackDate
  }

  /**
   * Get the number of date calculation errors encountered
   * @returns number - Number of date calculation errors
   */
  getDateCalculationErrors(): number {
    return this.dateCalculationErrors
  }

  /**
   * Calculates a deterministic word index from a date string with error handling
   * Uses a Linear Congruential Generator (LCG) approach to ensure maximum
   * distribution and no repeats for extended periods
   * @param dateString - Date in YYYY-MM-DD format
   * @returns number - Word index within word bank bounds
   */
  calculateWordIndex(dateString: string): number {
    try {
      // Validate input date string
      if (!dateString || typeof dateString !== 'string') {
        throw new Error(`Invalid date string: ${dateString}`)
      }
      
      if (!/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        throw new Error(`Invalid date format: ${dateString}`)
      }
      
      // Validate that it's a real date
      const testDate = new Date(dateString)
      if (isNaN(testDate.getTime())) {
        throw new Error(`Invalid date value: ${dateString}`)
      }
      
      // Get word count first for validation
      const wordCount = wordBankManager.getWordCount()
      if (wordCount === 0) {
        throw new Error('Word bank is empty. Cannot calculate word index.')
      }
      
      if (!Number.isFinite(wordCount) || wordCount < 1) {
        throw new Error(`Invalid word count: ${wordCount}`)
      }
      
      // Convert date to days since epoch (Jan 1, 2024) for consistent ordering
      const epochDate = new Date('2024-01-01')
      const currentDate = new Date(dateString)
      const daysSinceEpoch = Math.floor((currentDate.getTime() - epochDate.getTime()) / (1000 * 60 * 60 * 24))
      
      // Use Linear Congruential Generator for perfect distribution
      // This ensures each word is used exactly once before any repeats
      // LCG formula: (a * x + c) mod m
      // Where a and m are coprime for full period
      
      // Find a suitable 'a' value that's coprime with wordCount
      let a = this.findCoprime(wordCount)
      
      // Use a large prime as the additive constant
      const c = 1013904223
      
      // Apply LCG transformation
      let index = (a * daysSinceEpoch + c) % wordCount
      
      // Ensure positive result
      if (index < 0) {
        index += wordCount
      }
      
      // Additional bounds checking
      if (!Number.isFinite(index) || index < 0 || index >= wordCount) {
        // Index out of bounds, using fallback
        return 0 // Fallback to first word
      }
      
      return index
      
    } catch (error) {
      console.error('Error calculating word index:', error)
      
      // Fallback calculation using hash-based method
      try {
        // Use FNV-1a hash as fallback
        let fallbackHash = 2166136261
        
        for (let i = 0; i < dateString.length; i++) {
          const charCode = dateString.charCodeAt(i)
          fallbackHash ^= charCode
          fallbackHash = Math.imul(fallbackHash, 16777619)
        }
        
        fallbackHash = Math.abs(fallbackHash)
        
        const wordCount = wordBankManager.getWordCount()
        if (wordCount > 0) {
          const fallbackIndex = fallbackHash % wordCount
          // Using fallback word index calculation with FNV-1a hash
          return fallbackIndex
        }
      } catch (fallbackError) {
        console.error('Fallback word index calculation failed:', fallbackError)
      }
      
      // Final fallback: return 0 (first word)
      // Using emergency fallback word index
      return 0
    }
  }

  /**
   * Find a number coprime with the given number for LCG
   * Uses a simple approach to find a suitable multiplier
   * @param n - The modulus for the LCG
   * @returns number - A number coprime with n
   */
  private findCoprime(n: number): number {
    // For LCG, we want a multiplier that's coprime with the modulus
    // A simple approach: if n is even, use an odd number; if n is odd, use an even number
    // But we need to ensure they're actually coprime
    
    // Start with a good candidate based on Knuth's recommendations
    let candidate = Math.floor(n * 0.618033988749) // Golden ratio approximation
    if (candidate % 2 === 0) candidate += 1 // Make it odd if n might be even
    
    // Ensure it's coprime using GCD
    while (this.gcd(candidate, n) !== 1) {
      candidate += 2 // Try next odd number
      if (candidate >= n) {
        candidate = 3 // Reset to small prime if we've gone too far
      }
    }
    
    return candidate
  }

  /**
   * Calculate Greatest Common Divisor using Euclidean algorithm
   * @param a - First number
   * @param b - Second number
   * @returns number - GCD of a and b
   */
  private gcd(a: number, b: number): number {
    while (b !== 0) {
      const temp = b
      b = a % b
      a = temp
    }
    return a
  }

  /**
   * Checks if the date has changed since last calculation
   * Used to detect when a new daily word should be selected
   * @returns boolean - True if date has changed, false otherwise
   */
  checkForDateChange(): boolean {
    const newDate = this.getLocalDateString()
    const hasChanged = this.currentDate !== newDate
    

    
    return hasChanged
  }

  /**
   * Gets the current daily word
   * Calculates new word if date has changed or if not cached
   * @returns Promise<string> - The daily word
   */
  async getCurrentWord(): Promise<string> {
    const todayDate = this.getLocalDateString()
    const dateHasChanged = this.checkForDateChange()
    
    // Check if we need to calculate a new word
    if (dateHasChanged || !this.currentWord) {
      try {
        // Ensure word bank is loaded
        await wordBankManager.loadWordBank()
        
        // Calculate word index for today
        const wordIndex = this.calculateWordIndex(todayDate)
        
        // Get the word at the calculated index
        const word = wordBankManager.getWordByIndex(wordIndex)
        
        // Cache the results
        const previousDate = this.currentDate
        this.currentDate = todayDate
        this.currentWord = word
        this.cachedWordIndex = wordIndex
        
        // Daily word selected for today
        
        // Notify listeners if date actually changed (not just first load)
        if (dateHasChanged && previousDate) {
          this.notifyDateChangeListeners()
        }
        
        return word
      } catch (error) {
        console.error('Error calculating daily word:', error)
        
        // Fallback to a default word if calculation fails
        const fallbackWord = 'WOORD'
        this.currentWord = fallbackWord
        this.currentDate = todayDate
        
        return fallbackWord
      }
    }
    
    // Return cached word if date hasn't changed
    return this.currentWord
  }

  /**
   * Gets the cached word index for the current date
   * Useful for debugging and testing
   * @returns number - The current word index, -1 if not calculated
   */
  getCurrentWordIndex(): number {
    return this.cachedWordIndex
  }

  /**
   * Forces recalculation of the daily word
   * Useful for testing or manual refresh
   * @returns Promise<string> - The recalculated daily word
   */
  async forceRefresh(): Promise<string> {
    this.currentDate = ''
    this.currentWord = ''
    this.cachedWordIndex = -1
    return this.getCurrentWord()
  }

  /**
   * Adds a listener for date change events
   * Listeners will be called when a new day is detected
   * @param listener - Function to call when date changes
   */
  addDateChangeListener(listener: () => void): void {
    this.dateChangeListeners.push(listener)
  }

  /**
   * Removes a date change listener
   * @param listener - Function to remove from listeners
   */
  removeDateChangeListener(listener: () => void): void {
    const index = this.dateChangeListeners.indexOf(listener)
    if (index > -1) {
      this.dateChangeListeners.splice(index, 1)
    }
  }

  /**
   * Notifies all registered listeners that the date has changed
   * Called internally when a date change is detected
   */
  private notifyDateChangeListeners(): void {
    this.dateChangeListeners.forEach(listener => {
      try {
        listener()
      } catch (error) {
        console.error('Error in date change listener:', error)
      }
    })
  }

  /**
   * Starts automatic date change monitoring
   * Checks for date changes every minute and notifies listeners
   * @returns number - Interval ID for stopping the monitor
   */
  startDateChangeMonitoring(): number {
    const intervalId = window.setInterval(() => {
      if (this.checkForDateChange()) {
        this.notifyDateChangeListeners()
      }
    }, 60000) // Check every minute

    // Started daily word date change monitoring
    return intervalId
  }

  /**
   * Stops automatic date change monitoring
   * @param intervalId - The interval ID returned by startDateChangeMonitoring
   */
  stopDateChangeMonitoring(intervalId: number): void {
    window.clearInterval(intervalId)
    // Stopped daily word date change monitoring
  }
}

// Singleton instance for global use
export const dailyWordSelector = new DailyWordSelector()

/**
 * Utility function to get today's word
 * Provides a simple interface for the daily word selection
 * @returns Promise<string> - Today's daily word
 */
export async function getTodaysWord(): Promise<string> {
  return dailyWordSelector.getCurrentWord()
}

/**
 * Utility function to check if a new day has started
 * Can be used to trigger game resets
 * @returns boolean - True if date has changed since last check
 */
export function hasDateChanged(): boolean {
  return dailyWordSelector.checkForDateChange()
}

/**
 * Utility function to get the current date string
 * @returns string - Current date in YYYY-MM-DD format
 */
export function getCurrentDateString(): string {
  return dailyWordSelector.getLocalDateString()
}

/**
 * Utility function to start monitoring for date changes
 * @param callback - Function to call when date changes
 * @returns number - Interval ID for stopping the monitor
 */
export function startDailyWordMonitoring(callback?: () => void): number {
  if (callback) {
    dailyWordSelector.addDateChangeListener(callback)
  }
  return dailyWordSelector.startDateChangeMonitoring()
}

/**
 * Utility function to stop monitoring for date changes
 * @param intervalId - The interval ID returned by startDailyWordMonitoring
 * @param callback - Optional callback to remove from listeners
 */
export function stopDailyWordMonitoring(intervalId: number, callback?: () => void): void {
  if (callback) {
    dailyWordSelector.removeDateChangeListener(callback)
  }
  dailyWordSelector.stopDateChangeMonitoring(intervalId)
}